// app-ui/src/components/canvas/FlowDetailModal.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Modal from '../../../../../../shared/ui/Modal';
import { FlowNodeType, FlowNodeData } from './FlowNode';
import { getLatestBuild, getBuildHistory, BuildDetail } from '@/shared/lib/buildStatusTracker';
import { createComment, listComments, type Comment, type CommentTargetType } from '@/features/dashboard/projects/api/commentsApi';
import { getProjectMembers, type ProjectMember } from '@/features/dashboard/projects/api/projectsApi';
import { useAuth } from '@/features/system/auth/hooks/useAuth';
import DescriptionAttachmentUpload, {
    appendAttachmentSuffixToDescription,
} from './DescriptionAttachmentUpload';

interface FlowDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    nodeData: FlowNodeData | null;
    onSave?: (nodeData: FlowNodeData, updatedData: Partial<FlowNodeData>) => void;
    nodeType?: FlowNodeType;
    onBuildFeature?: (context: { nodeId: string }) => void;
    onBuildEpic?: (context: { nodeId: string }) => void;
    onGenerateChildReq?: (uiid: string) => void;
    onAddChildRequirement?: (parentUiid: string, parentNodeType: FlowNodeType) => void;
    onAddRequirement?: (parentUiid: string, nodeType: FlowNodeType) => void;
    onDeleteRequirement?: (uiid: string, nodeType: FlowNodeType) => void;
    projectId?: string | number;
    allNodesData?: any[]; // All nodes data to find children
    onNavigateToChild?: (childUiid: string, childNodeType: FlowNodeType) => void; // Navigate to child detail
    /** When set (e.g. from notification deep link), scroll this comment into view. */
    highlightCommentId?: string;
    /** When opening an LLR from outline, scroll and pulse this test case row. */
    highlightTestCaseUiid?: string | null;
}

// Configuration for different node types
const nodeTypeConfig: Record<FlowNodeType, {
    titlePrefix: string;
    nameLabel: string;
    badgeGradient: string;
    buttonClasses: string;
    focusRing: string;
}> = {
    userFlow: {
        titlePrefix: 'User Flow',
        nameLabel: 'Flow Name',
        badgeGradient: 'from-green-500 to-emerald-600',
        buttonClasses: 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700',
        focusRing: 'focus:ring-green-500'
    },
    highLevelRequirement: {
        titlePrefix: 'High Level Requirement',
        nameLabel: 'Requirement Name',
        badgeGradient: 'from-blue-500 to-cyan-600',
        buttonClasses: 'bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700',
        focusRing: 'focus:ring-blue-500'
    },
    lowLevelRequirement: {
        titlePrefix: 'Low Level Requirement',
        nameLabel: 'Requirement Name',
        badgeGradient: 'from-purple-500 to-violet-600',
        buttonClasses: 'bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700',
        focusRing: 'focus:ring-purple-500'
    },
    testCase: {
        titlePrefix: 'Test Case',
        nameLabel: 'Test Case Name',
        badgeGradient: 'from-orange-500 to-amber-600',
        buttonClasses: 'bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700',
        focusRing: 'focus:ring-orange-500'
    }
};

const FlowDetailModal: React.FC<FlowDetailModalProps> = ({
    isOpen,
    onClose,
    nodeData,
    onSave,
    nodeType: propNodeType,
    onBuildFeature,
    onBuildEpic,
    onGenerateChildReq,
    onAddChildRequirement,
    onAddRequirement,
    onDeleteRequirement,
    projectId,
    allNodesData,
    onNavigateToChild,
    highlightCommentId,
    highlightTestCaseUiid = null
}) => {
    const { user: authUser } = useAuth();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [showChildren, setShowChildren] = useState(true);
    const [showBuildStatusDropdown, setShowBuildStatusDropdown] = useState(false);
    const [showChildBuildStatusDropdown, setShowChildBuildStatusDropdown] = useState<number | null>(null);
    const buildStatusDropdownRef = useRef<HTMLDivElement>(null);
    const [showChildReqMenu, setShowChildReqMenu] = useState(false);
    const childReqMenuRef = useRef<HTMLDivElement>(null);

    // Comments and @mention state
    const [comments, setComments] = useState<Comment[]>([]);
    const [commentBody, setCommentBody] = useState('');
    const [taggedUserIds, setTaggedUserIds] = useState<string[]>([]);
    const [projectMembers, setProjectMembers] = useState<ProjectMember[]>([]);
    const [showMentionPicker, setShowMentionPicker] = useState(false);
    const [mentionFilter, setMentionFilter] = useState('');
    const [mentionPickerIndex, setMentionPickerIndex] = useState(0);
    const [loadingComments, setLoadingComments] = useState(false);
    const [submittingComment, setSubmittingComment] = useState(false);
    const commentInputRef = useRef<HTMLTextAreaElement>(null);
    const mentionPickerRef = useRef<HTMLDivElement>(null);
    const membersRequestIdRef = useRef(0);

    // Map FlowNodeType to API target_type
    const nodeTypeToTargetType: Record<FlowNodeType, CommentTargetType> = {
        userFlow: 'user_flow',
        highLevelRequirement: 'high_level_requirement',
        lowLevelRequirement: 'low_level_requirement',
        testCase: 'test_case'
    };

    // Determine node type: prop > data > default to userFlow
    const currentNodeType: FlowNodeType = propNodeType || nodeData?.nodeType || 'userFlow';
    const config = nodeTypeConfig[currentNodeType];

    // Get test cases from originalData for Low Level Requirements
    const testCases = currentNodeType === 'lowLevelRequirement' && nodeData?.originalData?.test_case_list 
        ? (nodeData.originalData.test_case_list || [])
        : [];

    // Find children based on the current node type
    const getChildren = (): { items: any[]; childType: FlowNodeType; label: string } => {
        if (!nodeData || !nodeData.originalData) {
            return { items: [], childType: 'userFlow', label: '' };
        }

        switch (currentNodeType) {
            case 'userFlow':
                // Children are High Level Requirements
                return {
                    items: nodeData.originalData.high_level_requirement_list || [],
                    childType: 'highLevelRequirement',
                    label: 'High Level Requirements'
                };
            case 'highLevelRequirement':
                // Children are Low Level Requirements
                return {
                    items: nodeData.originalData.low_level_requirement_list || [],
                    childType: 'lowLevelRequirement',
                    label: 'Low Level Requirements'
                };
            case 'lowLevelRequirement':
                // Children are Test Cases (already handled separately)
                return {
                    items: nodeData.originalData.test_case_list || [],
                    childType: 'testCase',
                    label: 'Test Cases'
                };
            default:
                return { items: [], childType: 'userFlow', label: '' };
        }
    };

    const childrenData = getChildren();

    useEffect(() => {
        if (nodeData) {
            setName(nodeData.name || '');
            setDescription(nodeData.description || '');
            setIsEditing(false);
        }
    }, [nodeData]);

    // Comments panel: scoped to the open requirement — do not carry draft or list across nodes/projects.
    const targetType = nodeTypeToTargetType[currentNodeType];
    useEffect(() => {
        setCommentBody('');
        setTaggedUserIds([]);
        setShowMentionPicker(false);
        setMentionFilter('');
        setMentionPickerIndex(0);
        setProjectMembers([]);
        setComments([]);
        setSubmittingComment(false);
    }, [isOpen, projectId, nodeData?.uiid, targetType]);

    // Fetch comments when modal opens with a node and project
    const fetchComments = useCallback(async () => {
        if (!projectId || !nodeData?.uiid || !isOpen) return;
        setLoadingComments(true);
        try {
            const res = await listComments(projectId, targetType, nodeData.uiid);
            setComments(res.comments || []);
        } catch {
            setComments([]);
        } finally {
            setLoadingComments(false);
        }
    }, [projectId, nodeData?.uiid, targetType, isOpen]);

    useEffect(() => {
        if (isOpen && projectId && nodeData?.uiid) {
            fetchComments();
        }
    }, [isOpen, projectId, nodeData?.uiid, fetchComments]);

    // Scroll to comment when opening from notification deep link
    useEffect(() => {
        if (!isOpen || !highlightCommentId || comments.length === 0) return;
        const el = document.querySelector(`[data-comment-id="${highlightCommentId}"]`);
        if (el) {
            const t = setTimeout(() => {
                el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 300);
            return () => clearTimeout(t);
        }
    }, [isOpen, highlightCommentId, comments]);

    const [flashTestCaseUiid, setFlashTestCaseUiid] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen || !highlightTestCaseUiid) {
            setFlashTestCaseUiid(null);
            return;
        }
        setFlashTestCaseUiid(highlightTestCaseUiid);
        const t = window.setTimeout(() => setFlashTestCaseUiid(null), 2000);
        return () => window.clearTimeout(t);
    }, [isOpen, highlightTestCaseUiid, nodeData?.uiid]);

    useEffect(() => {
        if (!isOpen || !highlightTestCaseUiid || testCases.length === 0) return;
        const t = window.setTimeout(() => {
            const el = document.querySelector(`[data-test-case-uiid="${CSS.escape(highlightTestCaseUiid)}"]`);
            el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 250);
        return () => window.clearTimeout(t);
    }, [isOpen, highlightTestCaseUiid, testCases.length]);

    // Fetch project members for @mention — refetch each time user focuses the comment box or opens @ picker
    // so newly shared users appear (no long-lived cache).
    const loadProjectMembers = useCallback(async () => {
        if (!projectId) return;
        const reqId = ++membersRequestIdRef.current;
        try {
            const res = await getProjectMembers(projectId, { forTagging: true });
            if (reqId === membersRequestIdRef.current) {
                setProjectMembers(res.members || []);
            }
        } catch {
            if (reqId === membersRequestIdRef.current) {
                setProjectMembers([]);
            }
        }
    }, [projectId]);

    // Comment input: detect @ and show mention picker
    const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setCommentBody(value);
        const cursorPos = e.target.selectionStart ?? value.length;
        const textBeforeCursor = value.slice(0, cursorPos);
        const atMatch = textBeforeCursor.match(/@([^\s]*)$/);
        if (atMatch) {
            setMentionFilter(atMatch[1].toLowerCase());
            setShowMentionPicker(true);
            setMentionPickerIndex(0);
            void loadProjectMembers();
        } else {
            setShowMentionPicker(false);
        }
    };

    const filteredMembers = projectMembers.filter(
        (m) =>
            !mentionFilter ||
            `${m.name || ''} ${m.email || ''} ${m.user_id}`.toLowerCase().includes(mentionFilter)
    ).slice(0, 8);

    const insertMention = (member: ProjectMember) => {
        const cursorPos = commentInputRef.current?.selectionStart ?? commentBody.length;
        const textBeforeCursor = commentBody.slice(0, cursorPos);
        const textAfterCursor = commentBody.slice(cursorPos);
        const atStart = textBeforeCursor.lastIndexOf('@');
        const beforeAt = commentBody.slice(0, atStart);
        const displayName = member.name || member.email || member.user_id.slice(0, 8);
        const newBody = `${beforeAt}@${displayName} ${textAfterCursor}`;
        setCommentBody(newBody);
        if (!taggedUserIds.includes(member.user_id)) {
            setTaggedUserIds((prev) => [...prev, member.user_id]);
        }
        setShowMentionPicker(false);
        setMentionFilter('');
        setTimeout(() => commentInputRef.current?.focus(), 0);
    };

    const handleSubmitComment = async () => {
        const body = commentBody.trim();
        if (!body || !projectId || !nodeData?.uiid) return;
        setSubmittingComment(true);
        try {
            await createComment(projectId, {
                target_type: targetType,
                target_uiid: nodeData.uiid,
                body,
                tagged_user_ids: taggedUserIds,
                ...(authUser?.email ? { author_display_email: authUser.email } : {}),
                ...(authUser?.name ? { author_display_name: authUser.name } : {}),
            });
            setCommentBody('');
            setTaggedUserIds([]);
            await fetchComments();
        } catch (err) {
            console.error('Failed to post comment:', err);
        } finally {
            setSubmittingComment(false);
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (buildStatusDropdownRef.current && !buildStatusDropdownRef.current.contains(event.target as Node)) {
                setShowBuildStatusDropdown(false);
                setShowChildBuildStatusDropdown(null);
            }
        };

        if (showBuildStatusDropdown || showChildBuildStatusDropdown !== null) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showBuildStatusDropdown, showChildBuildStatusDropdown]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (childReqMenuRef.current && !childReqMenuRef.current.contains(event.target as Node)) {
                setShowChildReqMenu(false);
            }
        };
        if (showChildReqMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showChildReqMenu]);

    const handleSave = () => {
        if (nodeData && onSave) {
            onSave(nodeData, {
                name,
                description,
                nodeType: currentNodeType
            });
        }
        setIsEditing(false);
    };

    const handleCancel = () => {
        if (nodeData) {
            setName(nodeData.name || '');
            setDescription(nodeData.description || '');
        }
        setIsEditing(false);
    };

    if (!nodeData) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`${config.titlePrefix} Details - Step ${nodeData.sno}`}
            size="lg"
            draggable
            resizable
        >
            <div className="flex flex-col flex-1 min-h-0">
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain space-y-6 pr-1">
                {/* Step Number Display */}
                <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 bg-gradient-to-br ${config.badgeGradient} text-white rounded-full flex items-center justify-center text-sm font-bold`}>
                        {nodeData.sno}
                    </div>
                    <div className="flex-1">
                        <div className="text-sm">
                            <div className="flex items-center justify-between gap-3 mb-2">
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-400 font-semibold text-base">UIID:</span>
                                    <span className="font-mono text-white font-medium text-base">{nodeData.uiid}</span>
                                </div>
                                {/* Build Status Button - Only for Low Level Requirements - Moved to right */}
                                {currentNodeType === 'lowLevelRequirement' && projectId && (() => {
                                    // Get build status from localStorage tracking
                                    const trackedLatestBuild = getLatestBuild(projectId, nodeData.uiid || '');
                                    const trackedHistory = getBuildHistory(projectId, nodeData.uiid || '');
                                    
                                    // If no tracked entries, show "Not Started" and empty dropdown
                                    const hasTrackedEntries = trackedLatestBuild !== null;
                                    
                                    // Use tracked data if available, otherwise fall back to originalData or "Not Started"
                                    const buildStatus = hasTrackedEntries
                                        ? (trackedLatestBuild?.build_status || 'Not Started')
                                        : (nodeData.originalData?.build_status || 'Not Started');
                                    const buildInitiatedAt = hasTrackedEntries
                                        ? (trackedLatestBuild?.build_initiated_at || null)
                                        : (nodeData.originalData?.build_initiated_at || nodeData.originalData?.launched_at || nodeData.originalData?.launch_date || null);
                                    const gitCommit = hasTrackedEntries
                                        ? (trackedLatestBuild?.git_commit || null)
                                        : (nodeData.originalData?.git_commit || null);
                                    const buildHistory = trackedHistory.length > 0 ? trackedHistory : (nodeData.originalData?.build_history || []);
                                    
                                    // Determine button color based on status
                                    const getStatusButtonColor = () => {
                                        if (buildStatus === 'Completed') return 'bg-green-600 hover:bg-green-700 text-white';
                                        if (buildStatus === 'In Progress') return 'bg-blue-600 hover:bg-blue-700 text-white';
                                        if (buildStatus === 'Error') return 'bg-red-600 hover:bg-red-700 text-white';
                                        return 'bg-gray-600 hover:bg-gray-700 text-white'; // Not Started
                                    };
                                    
                                    const formatDate = (dateString: string | null | undefined): string => {
                                        if (!dateString) return 'N/A';
                                        try {
                                            const date = new Date(dateString);
                                            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                                        } catch {
                                            return 'N/A';
                                        }
                                    };
                                    
                                    // Sort history by build_initiated_at descending (for tracked builds) or date (for originalData)
                                    const sortedHistory = trackedHistory.length > 0 
                                        ? trackedHistory
                                        : [...buildHistory].sort((a: any, b: any) => {
                                            const dateA = a.timestamp || a.date || a.created_at || a.build_initiated_at || 0;
                                            const dateB = b.timestamp || b.date || b.created_at || b.build_initiated_at || 0;
                                            return new Date(dateB).getTime() - new Date(dateA).getTime();
                                        });
                                    
                                        return (
                                        <div className="relative" ref={buildStatusDropdownRef}>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setShowBuildStatusDropdown(!showBuildStatusDropdown);
                                                }}
                                                className={`
                                                    px-4 py-2 rounded-lg text-sm font-semibold transition-all
                                                    flex items-center gap-2
                                                    ${getStatusButtonColor()}
                                                `}
                                            >
                                                <span>{buildStatus}</span>
                                                <svg 
                                                    className={`w-4 h-4 transition-transform ${showBuildStatusDropdown ? 'rotate-180' : ''}`}
                                                    fill="none" 
                                                    stroke="currentColor" 
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </button>
                                            
                                            {showBuildStatusDropdown && (
                                                <div className="absolute right-0 mt-2 w-72 bg-gray-800 border border-white/20 rounded-lg shadow-xl z-50 p-4">
                                                    {hasTrackedEntries || buildHistory.length > 0 ? (
                                                        <div className="space-y-3">
                                                            <div className="border-b border-white/10 pb-2">
                                                                <div className="text-xs font-semibold text-gray-400 mb-1">Git Commit</div>
                                                                <div className={`font-mono text-sm ${gitCommit ? 'text-green-400' : 'text-gray-400'}`}>
                                                                    {gitCommit ? gitCommit.substring(0, 7) : 'N/A'}
                                                                </div>
                                                            </div>
                                                            <div className="border-b border-white/10 pb-2">
                                                                <div className="text-xs font-semibold text-gray-400 mb-1">Build Initiated</div>
                                                                <div className="text-sm text-white">
                                                                    {formatDate(buildInitiatedAt)}
                                                                </div>
                                                            </div>
                                                            <div className="border-b border-white/10 pb-2">
                                                                <div className="text-xs font-semibold text-gray-400 mb-1">Build Status</div>
                                                                <div className={`text-sm font-semibold ${
                                                                    buildStatus === 'Completed' ? 'text-green-400' :
                                                                    buildStatus === 'In Progress' ? 'text-blue-400' :
                                                                    buildStatus === 'Error' ? 'text-red-400' :
                                                                    'text-white'
                                                                }`}>
                                                                    {buildStatus}
                                                                </div>
                                                            </div>
                                                        {sortedHistory.length > 0 && (
                                                            <div>
                                                                <div className="text-xs font-semibold text-gray-400 mb-2">History</div>
                                                                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                                                                    {sortedHistory.map((entry: BuildDetail | any, idx: number) => {
                                                                        // Handle both BuildDetail format and originalData format
                                                                        const entryStatus = entry.build_status || entry.status || 'Unknown';
                                                                        const entryDate = entry.build_initiated_at || entry.timestamp || entry.date || entry.created_at;
                                                                        const entryCommit = entry.git_commit;
                                                                        return (
                                                                            <div key={idx} className="text-xs p-2 bg-white/5 rounded border border-white/10">
                                                                                <div className="flex justify-between items-start mb-1">
                                                                                    <span className={`font-semibold ${
                                                                                        entryStatus === 'Completed' ? 'text-green-400' :
                                                                                        entryStatus === 'In Progress' ? 'text-blue-400' :
                                                                                        entryStatus === 'Error' ? 'text-red-400' :
                                                                                        'text-white'
                                                                                    }`}>
                                                                                        {entryStatus}
                                                                                    </span>
                                                                                    <span className="text-gray-400 text-xs">
                                                                                        {formatDate(entryDate)}
                                                                                    </span>
                                                                                </div>
                                                                                {entryCommit && (
                                                                                    <div className="text-xs text-gray-300 font-mono">
                                                                                        {typeof entryCommit === 'string' ? entryCommit.substring(0, 7) : entryCommit}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        )}
                                                        </div>
                                                    ) : (
                                                        <div className="text-sm text-gray-400 text-center py-4">
                                                            No Build Entries
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                                <div className="flex items-center gap-1.5">
                                    <span className="text-gray-400 font-semibold text-xs">Version:</span>
                                    <span className="text-white font-medium text-xs">{nodeData.originalData?.version || '1.0'}</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <span className="text-gray-400 font-semibold text-xs">Updated:</span>
                                    <span className="text-white font-medium text-xs">{(() => {
                                        const updatedAt = nodeData.originalData?.updated_at || nodeData.originalData?.created_at;
                                        if (!updatedAt) return 'N/A';
                                        try {
                                            const date = new Date(updatedAt);
                                            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                                        } catch {
                                            return 'N/A';
                                        }
                                    })()}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Name Field */}
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                        {config.nameLabel}
                    </label>
                    {isEditing ? (
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className={`w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 ${config.focusRing} focus:border-transparent`}
                            placeholder={`Enter ${config.nameLabel.toLowerCase()}`}
                        />
                    ) : (
                        <div className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white min-h-[40px] flex items-center">
                            {name || 'No name provided'}
                        </div>
                    )}
                </div>

                {/* Description Field */}
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                        Description
                    </label>
                    {isEditing ? (
                        <>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={currentNodeType === 'lowLevelRequirement' ? 12 : 6}
                            className={`w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 ${config.focusRing} focus:border-transparent resize-none`}
                            placeholder="Enter description"
                        />
                        <DescriptionAttachmentUpload
                            enabled={isEditing}
                            projectId={projectId}
                            onAppend={(fileName, s3Path) =>
                                setDescription((prev) =>
                                    appendAttachmentSuffixToDescription(prev, fileName, s3Path)
                                )
                            }
                        />
                        </>
                    ) : (
                        <div
                            className={`px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white whitespace-pre-wrap ${
                                currentNodeType === 'lowLevelRequirement' ? 'min-h-[260px]' : 'min-h-[120px]'
                            }`}
                        >
                            {description || 'No description provided'}
                        </div>
                    )}
                </div>

                {/* Children Section - Display child requirements/test cases */}
                {childrenData.items.length > 0 && currentNodeType !== 'lowLevelRequirement' && (
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-300">
                                {childrenData.label} ({childrenData.items.length})
                            </label>
                            <button
                                onClick={() => setShowChildren(!showChildren)}
                                className="text-xs text-gray-400 hover:text-white transition-colors flex items-center gap-1"
                            >
                                {showChildren ? (
                                    <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                        Hide
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                        Show
                                    </>
                                )}
                            </button>
                        </div>
                        {showChildren && (
                            <div className="px-4 py-3 bg-white/5 border border-white/10 rounded-lg max-h-[300px] overflow-y-auto">
                                {childrenData.items.length > 0 ? (
                                    <div className="space-y-2">
                                        {childrenData.items.map((child: any, index: number) => {
                                            const childUiid = child.uiid || child.id || `child-${index}`;
                                            const childName = child.name || child.requirement_text || child.description || `${childrenData.label} ${index + 1}`;
                                            const childDesc = child.description || child.desc || '';
                                            const childSno = child.sno || index + 1;
                                            const childVersion = child.version || child.originalData?.version || '1.0';
                                            const childUpdatedAt = child.updated_at || child.originalData?.updated_at || child.created_at || child.originalData?.created_at;
                                            const childGitCommit = child.git_commit || child.originalData?.git_commit;
                                            const childLaunchedAt = child.launched_at || child.originalData?.launched_at || child.launch_date || child.originalData?.launch_date;
                                            
                                            // Format the date
                                            const formatDate = (dateString: string | null | undefined): string => {
                                                if (!dateString) return 'N/A';
                                                try {
                                                    const date = new Date(dateString);
                                                    return date.toLocaleDateString('en-US', { 
                                                        year: 'numeric', 
                                                        month: 'short', 
                                                        day: 'numeric' 
                                                    });
                                                } catch {
                                                    return 'N/A';
                                                }
                                            };
                                            
                                            return (
                                                <div
                                                    key={childUiid}
                                                    className={`p-3 rounded-lg border transition-all cursor-pointer ${
                                                        childrenData.childType === 'highLevelRequirement'
                                                            ? 'bg-blue-500/10 border-blue-500/30 hover:bg-blue-500/20'
                                                            : childrenData.childType === 'lowLevelRequirement'
                                                            ? 'bg-purple-500/10 border-purple-500/30 hover:bg-purple-500/20'
                                                            : 'bg-orange-500/10 border-orange-500/30 hover:bg-orange-500/20'
                                                    }`}
                                                    onClick={() => {
                                                        if (onNavigateToChild) {
                                                            onNavigateToChild(childUiid, childrenData.childType);
                                                        }
                                                    }}
                                                    title="Click to view details"
                                                >
                                                    <div className="flex items-start gap-3">
                                                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                                                            childrenData.childType === 'highLevelRequirement'
                                                                ? 'bg-blue-500 text-white'
                                                                : childrenData.childType === 'lowLevelRequirement'
                                                                ? 'bg-purple-500 text-white'
                                                                : 'bg-orange-500 text-white'
                                                        }`}>
                                                            {childSno}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-sm font-medium text-white truncate">
                                                                {childName}
                                                            </div>
                                                            {childDesc && (
                                                                <div className="text-xs text-gray-400 mt-1 line-clamp-2">
                                                                    {childDesc}
                                                                </div>
                                                            )}
                                                            <div className="text-xs mt-2">
                                                                <div className="flex items-center justify-between gap-2 mb-1">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-gray-400 font-semibold text-sm">UIID:</span>
                                                                        <span className="font-mono text-white font-medium text-sm">{childUiid}</span>
                                                                    </div>
                                                                    {/* Build Status Button - Only for Low Level Requirements - Moved to right */}
                                                                    {childrenData.childType === 'lowLevelRequirement' && projectId && (() => {
                                                                        // Get build status from localStorage tracking
                                                                        const trackedLatestBuild = getLatestBuild(projectId, childUiid);
                                                                        const trackedHistory = getBuildHistory(projectId, childUiid);
                                                                        
                                                                        // If no tracked entries, show "Not Started" and empty dropdown
                                                                        const hasTrackedEntries = trackedLatestBuild !== null;
                                                                        
                                                                        // Use tracked data if available, otherwise fall back to originalData or "Not Started"
                                                                        const childBuildStatus = hasTrackedEntries
                                                                            ? (trackedLatestBuild?.build_status || 'Not Started')
                                                                            : (child.build_status || child.originalData?.build_status || 'Not Started');
                                                                        const childBuildInitiatedAt = hasTrackedEntries
                                                                            ? (trackedLatestBuild?.build_initiated_at || null)
                                                                            : (child.launched_at || child.originalData?.launched_at || child.launch_date || child.originalData?.launch_date || child.originalData?.build_initiated_at || null);
                                                                        const childGitCommitValue = hasTrackedEntries
                                                                            ? (trackedLatestBuild?.git_commit || null)
                                                                            : childGitCommit;
                                                                        const childBuildHistory = trackedHistory.length > 0 ? trackedHistory : (child.build_history || child.originalData?.build_history || []);
                                                                        
                                                                        // Determine button color based on status
                                                                        const getChildStatusButtonColor = () => {
                                                                            if (childBuildStatus === 'Completed') return 'bg-green-600 hover:bg-green-700 text-white';
                                                                            if (childBuildStatus === 'In Progress') return 'bg-blue-600 hover:bg-blue-700 text-white';
                                                                            if (childBuildStatus === 'Error') return 'bg-red-600 hover:bg-red-700 text-white';
                                                                            return 'bg-gray-600 hover:bg-gray-700 text-white'; // Not Started
                                                                        };
                                                                        
                                                                        // Sort history by build_initiated_at descending (for tracked builds) or date (for originalData)
                                                                        const sortedChildHistory = trackedHistory.length > 0 
                                                                            ? trackedHistory
                                                                            : [...childBuildHistory].sort((a: any, b: any) => {
                                                                                const dateA = a.timestamp || a.date || a.created_at || a.build_initiated_at || 0;
                                                                                const dateB = b.timestamp || b.date || b.created_at || b.build_initiated_at || 0;
                                                                                return new Date(dateB).getTime() - new Date(dateA).getTime();
                                                                            });
                                                                        
                                                                        return (
                                                                            <div className="relative">
                                                                                <button
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setShowChildBuildStatusDropdown(showChildBuildStatusDropdown === index ? null : index);
                                                                                    }}
                                                                                    className={`
                                                                                        px-3 py-1.5 rounded-lg text-xs font-semibold transition-all
                                                                                        flex items-center gap-2
                                                                                        ${getChildStatusButtonColor()}
                                                                                    `}
                                                                                >
                                                                                    <span>{childBuildStatus}</span>
                                                                                    <svg 
                                                                                        className={`w-3 h-3 transition-transform ${showChildBuildStatusDropdown === index ? 'rotate-180' : ''}`}
                                                                                        fill="none" 
                                                                                        stroke="currentColor" 
                                                                                        viewBox="0 0 24 24"
                                                                                    >
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                                                    </svg>
                                                                                </button>
                                                                                
                                                                                {showChildBuildStatusDropdown === index && (
                                                                                    <div className="absolute right-0 mt-1 w-64 bg-gray-800 border border-white/20 rounded-lg shadow-xl z-50 p-3">
                                                                                        {hasTrackedEntries || childBuildHistory.length > 0 ? (
                                                                                            <div className="space-y-2">
                                                                                                <div className="border-b border-white/10 pb-2">
                                                                                                    <div className="text-xs font-semibold text-gray-400 mb-1">Git Commit</div>
                                                                                                    <div className={`font-mono text-sm ${childGitCommitValue ? 'text-green-400' : 'text-gray-400'}`}>
                                                                                                        {childGitCommitValue ? childGitCommitValue.substring(0, 7) : 'N/A'}
                                                                                                    </div>
                                                                                                </div>
                                                                                                <div className="border-b border-white/10 pb-2">
                                                                                                    <div className="text-xs font-semibold text-gray-400 mb-1">Build Initiated</div>
                                                                                                    <div className="text-sm text-white">
                                                                                                        {formatDate(childBuildInitiatedAt)}
                                                                                                    </div>
                                                                                                </div>
                                                                                                <div className="border-b border-white/10 pb-2">
                                                                                                    <div className="text-xs font-semibold text-gray-400 mb-1">Build Status</div>
                                                                                                    <div className={`text-sm font-semibold ${
                                                                                                        childBuildStatus === 'Completed' ? 'text-green-400' :
                                                                                                        childBuildStatus === 'In Progress' ? 'text-blue-400' :
                                                                                                        childBuildStatus === 'Error' ? 'text-red-400' :
                                                                                                        'text-white'
                                                                                                    }`}>
                                                                                                        {childBuildStatus}
                                                                                                    </div>
                                                                                                </div>
                                                                                            {sortedChildHistory.length > 0 && (
                                                                                                <div>
                                                                                                    <div className="text-xs font-semibold text-gray-400 mb-2">History</div>
                                                                                                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                                                                                                        {sortedChildHistory.map((entry: BuildDetail | any, idx: number) => {
                                                                                                            // Handle both BuildDetail format and originalData format
                                                                                                            const entryStatus = entry.build_status || entry.status || 'Unknown';
                                                                                                            const entryDate = entry.build_initiated_at || entry.timestamp || entry.date || entry.created_at;
                                                                                                            const entryCommit = entry.git_commit;
                                                                                                            return (
                                                                                                                <div key={idx} className="text-xs p-2 bg-white/5 rounded border border-white/10">
                                                                                                                    <div className="flex justify-between items-start mb-1">
                                                                                                                        <span className={`font-semibold ${
                                                                                                                            entryStatus === 'Completed' ? 'text-green-400' :
                                                                                                                            entryStatus === 'In Progress' ? 'text-blue-400' :
                                                                                                                            entryStatus === 'Error' ? 'text-red-400' :
                                                                                                                            'text-white'
                                                                                                                        }`}>
                                                                                                                            {entryStatus}
                                                                                                                        </span>
                                                                                                                        <span className="text-gray-400 text-xs">
                                                                                                                            {formatDate(entryDate)}
                                                                                                                        </span>
                                                                                                                    </div>
                                                                                                                    {entryCommit && (
                                                                                                                        <div className="text-xs text-gray-300 font-mono">
                                                                                                                            {typeof entryCommit === 'string' ? entryCommit.substring(0, 7) : entryCommit}
                                                                                                                        </div>
                                                                                                                    )}
                                                                                                                </div>
                                                                                                            );
                                                                                                        })}
                                                                                                    </div>
                                                                                                </div>
                                                                                            )}
                                                                                            </div>
                                                    ) : (
                                                        <div className="text-sm text-gray-400 text-center py-4">
                                                            No Build Entries
                                                        </div>
                                                    )}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </div>
                                                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
                                                                    <div className="flex items-center gap-1.5">
                                                                        <span className="text-gray-400 font-semibold text-xs">Version:</span>
                                                                        <span className="text-white font-medium text-xs">{childVersion}</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-1.5">
                                                                        <span className="text-gray-400 font-semibold text-xs">Updated:</span>
                                                                        <span className="text-white font-medium text-xs">{formatDate(childUpdatedAt)}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <svg className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-gray-400 text-sm">No {childrenData.label.toLowerCase()} available</div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Test Cases Field - Only for Low Level Requirements */}
                {currentNodeType === 'lowLevelRequirement' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Test Cases
                        </label>
                        {isEditing ? (
                            <textarea
                                value={testCases.map((tc: any) => `• ${tc.name}${tc.description ? ` - ${tc.description}` : ''}`).join('\n')}
                                readOnly
                                rows={Math.max(4, Math.min(10, testCases.length + 1))}
                                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 resize-none cursor-not-allowed"
                                placeholder="Test cases will be displayed here"
                            />
                        ) : (
                            <div className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white min-h-[100px] max-h-[300px] overflow-y-auto">
                                {testCases.length > 0 ? (
                                    <ul className="list-none space-y-2 pl-0">
                                        {testCases.map((tc: any, index: number) => {
                                            const tcUiid = String(tc.uiid ?? tc.id ?? index);
                                            const isFlash = flashTestCaseUiid === tcUiid;
                                            return (
                                                <li
                                                    key={tc.uiid || tc.id || index}
                                                    data-test-case-uiid={tcUiid}
                                                    className={`rounded-md px-2 py-1.5 text-sm transition-shadow ${
                                                        isFlash
                                                            ? 'bg-indigo-500/20 ring-2 ring-indigo-300/90 shadow-[0_0_24px_rgba(129,140,248,0.45)]'
                                                            : ''
                                                    }`}
                                                >
                                                    <span className="font-medium text-white">{tc.name}</span>
                                                    {tc.description && (
                                                        <span className="text-gray-400"> - {tc.description}</span>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                ) : (
                                    <div className="text-gray-400">No test cases available</div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Comments Section */}
                {projectId && nodeData?.uiid && (
                    <div className="border-t border-white/10 pt-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Comments
                        </label>
                        {loadingComments ? (
                            <div className="text-sm text-gray-400 py-2">Loading comments…</div>
                        ) : (
                            <div className="space-y-2 max-h-40 overflow-y-auto mb-3">
                                {comments.length === 0 ? (
                                    <div className="text-sm text-gray-400">No comments yet.</div>
                                ) : (
                                    comments.map((c) => (
                                        <div
                                            key={c.comment_id}
                                            data-comment-id={c.comment_id}
                                            className="p-3 bg-white/5 border border-white/10 rounded-lg text-sm"
                                        >
                                            <div className="flex items-center gap-2 text-gray-400 mb-1">
                                                <span className="font-medium text-white">{c.author_email || c.author_name || c.author_user_id}</span>
                                                <span>{new Date(c.created_at).toLocaleString()}</span>
                                            </div>
                                            <p className="text-white whitespace-pre-wrap">{c.body}</p>
                                            {c.tagged_user_ids && c.tagged_user_ids.length > 0 && (
                                                <div className="text-xs text-gray-400 mt-1">
                                                    Tagged: {c.tagged_user_ids.length} user(s)
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                        <div className="relative">
                            <textarea
                                ref={commentInputRef}
                                value={commentBody}
                                onChange={handleCommentChange}
                                onFocus={() => {
                                    void loadProjectMembers();
                                }}
                                onKeyDown={(e) => {
                                    if (showMentionPicker && filteredMembers.length > 0) {
                                        if (e.key === 'ArrowDown') {
                                            e.preventDefault();
                                            setMentionPickerIndex((i) => (i + 1) % filteredMembers.length);
                                        } else if (e.key === 'ArrowUp') {
                                            e.preventDefault();
                                            setMentionPickerIndex((i) => (i - 1 + filteredMembers.length) % filteredMembers.length);
                                        } else if (e.key === 'Enter' && filteredMembers.length > 0) {
                                            e.preventDefault();
                                            insertMention(filteredMembers[mentionPickerIndex]);
                                        } else if (e.key === 'Escape') {
                                            setShowMentionPicker(false);
                                        }
                                    }
                                }}
                                placeholder="Add a comment… Type @ to tag someone"
                                rows={3}
                                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                            />
                            {showMentionPicker && (
                                <div
                                    ref={mentionPickerRef}
                                    className="absolute left-0 right-0 bottom-full mb-1 bg-gray-800 border border-white/20 rounded-lg shadow-xl z-50 overflow-hidden max-h-48 overflow-y-auto"
                                >
                                    {filteredMembers.length === 0 ? (
                                        <div className="px-3 py-2 text-sm text-gray-400">No matching members</div>
                                    ) : (
                                        filteredMembers.map((m, i) => (
                                            <button
                                                key={m.user_id}
                                                type="button"
                                                onClick={() => insertMention(m)}
                                                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                                                    i === mentionPickerIndex ? 'bg-blue-600/30 text-white' : 'text-gray-200 hover:bg-white/10'
                                                }`}
                                            >
                                                <span>{m.name || m.email || m.user_id.slice(0, 8)}</span>
                                                {m.email && (
                                                    <span className="text-gray-500 text-xs truncate max-w-[140px]">
                                                        {m.email}
                                                    </span>
                                                )}
                                                <span className="text-gray-500 text-xs">({m.permission})</span>
                                            </button>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={handleSubmitComment}
                            disabled={!commentBody.trim() || submittingComment}
                            className="mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                        >
                            {submittingComment ? 'Posting…' : 'Post comment'}
                        </button>
                    </div>
                )}
                </div>

                {/* Action Buttons */}
                <div className="flex-shrink-0 flex justify-between items-center pt-4 mt-4 border-t border-white/10">
                    {/* Left side - Add/Delete buttons */}
                    <div className="flex gap-2">
                        {/* Add Requirement button - for userFlow, highLevelRequirement, and lowLevelRequirement */}
                        {currentNodeType !== 'testCase' && onAddRequirement && (
                            <button
                                onClick={() => {
                                    if (onAddRequirement && nodeData?.uiid) {
                                        onAddRequirement(nodeData.uiid, currentNodeType);
                                    }
                                }}
                                className="w-10 h-10 flex items-center justify-center bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-lg transition-all shadow-lg hover:shadow-green-500/25"
                                title="Add a new requirement at this level"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                            </button>
                        )}
                        {/* Delete Requirement button */}
                        {onDeleteRequirement && (
                            <button
                                onClick={() => {
                                    if (onDeleteRequirement && nodeData?.uiid) {
                                        if (window.confirm(`Are you sure you want to delete this ${config.titlePrefix.toLowerCase()}? This action cannot be undone.`)) {
                                            onDeleteRequirement(nodeData.uiid, currentNodeType);
                                            onClose();
                                        }
                                    }
                                }}
                                className="w-10 h-10 flex items-center justify-center bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg transition-all shadow-lg hover:shadow-red-500/25"
                                title="Delete this requirement"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </button>
                        )}
                    </div>

                    {/* Right side - Edit and other action buttons */}
                    <div className="flex gap-2">
                        {isEditing ? (
                            <>
                                <button
                                    onClick={handleCancel}
                                    className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                                    title="Cancel editing"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                                <button
                                    onClick={handleSave}
                                    className={`w-10 h-10 flex items-center justify-center ${config.buttonClasses} text-white rounded-lg transition-all shadow-lg hover:shadow-purple-500/25`}
                                    title="Save changes"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </button>
                            </>
                        ) : (
                            <>
                                {/* Child requirements: generate with AI or add manually */}
                                {currentNodeType !== 'testCase' &&
                                    (onGenerateChildReq || onAddChildRequirement) && (
                                    <div
                                        className="relative"
                                        ref={childReqMenuRef}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => setShowChildReqMenu((open) => !open)}
                                            className="w-10 h-10 flex items-center justify-center bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700 text-white rounded-lg transition-all shadow-lg hover:shadow-purple-500/25"
                                            title="Add child requirements"
                                            aria-expanded={showChildReqMenu}
                                            aria-haspopup="menu"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                            </svg>
                                        </button>
                                        {showChildReqMenu && (
                                            <div
                                                role="menu"
                                                className="absolute bottom-full left-0 mb-2 z-50 min-w-[220px] py-1 rounded-lg border border-white/15 bg-gray-900/95 shadow-xl backdrop-blur-sm"
                                            >
                                                {onGenerateChildReq && (
                                                    <button
                                                        type="button"
                                                        role="menuitem"
                                                        className="w-full px-3 py-2 text-left text-sm text-white hover:bg-white/10 transition-colors flex items-center gap-2"
                                                        onClick={() => {
                                                            setShowChildReqMenu(false);
                                                            onGenerateChildReq(nodeData.uiid || nodeData.id);
                                                        }}
                                                    >
                                                        <svg className="w-4 h-4 text-purple-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                                        </svg>
                                                        Generate with AI
                                                    </button>
                                                )}
                                                {onAddChildRequirement && (
                                                    <button
                                                        type="button"
                                                        role="menuitem"
                                                        className="w-full px-3 py-2 text-left text-sm text-white hover:bg-white/10 transition-colors flex items-center gap-2"
                                                        onClick={() => {
                                                            setShowChildReqMenu(false);
                                                            onAddChildRequirement(
                                                                nodeData.uiid || nodeData.id,
                                                                currentNodeType
                                                            );
                                                        }}
                                                    >
                                                        <svg className="w-4 h-4 text-emerald-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                        </svg>
                                                        Add manually
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {/* Build Epic button - only for High Level Requirement */}
                                {currentNodeType === 'highLevelRequirement' && onBuildEpic && (
                                    <button
                                        onClick={() => {
                                            onBuildEpic({ nodeId: nodeData.id });
                                            onClose();
                                        }}
                                        className="w-10 h-10 flex items-center justify-center bg-green-600/20 hover:bg-green-600/30 text-green-400 hover:text-green-300 border border-green-500/30 rounded-lg transition-all"
                                        title="Build Epic"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                    </button>
                                )}
                                {/* Build Feature - only for Low Level Requirement */}
                                {currentNodeType === 'lowLevelRequirement' && onBuildFeature && (
                                    <button
                                        onClick={() => {
                                            onBuildFeature({ nodeId: nodeData.id });
                                            onClose();
                                        }}
                                        className="w-10 h-10 flex items-center justify-center bg-green-600/20 hover:bg-green-600/30 text-green-400 hover:text-green-300 border border-green-500/30 rounded-lg transition-all"
                                        title="Build the Feature"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                    </button>
                                )}
                                {onSave && (
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className={`w-10 h-10 flex items-center justify-center ${config.buttonClasses} text-white rounded-lg transition-all shadow-lg hover:shadow-purple-500/25`}
                                    title="Edit"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default FlowDetailModal;

